import queryString from 'query-string';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Args<TName extends string> = {
  names: TName[];
};

export function useMultipleQueryParamStates<TName extends string>({
  names,
}: Args<TName>): [
  Partial<Record<TName, string>>,
  (values: Partial<Record<TName, string | undefined>>) => void,
] {
  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = queryString.parse(location.search);

  const values = names.reduce((acc, name) => {
    const rawValue = searchParams[name];
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    return { ...acc, [name]: value || undefined };
  }, {});

  const setValues = useCallback(
    (newValues: Partial<Record<TName, string | undefined>>) => {
      const search = queryString.stringify({ ...searchParams, ...newValues });
      navigate({ pathname: location.pathname, hash: location.hash, search });
    },
    [navigate, location, searchParams],
  );

  return [values, setValues];
}
